import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Modal } from 'antd';

import TagBadge, { TagRow } from './TagBadge';
import TagSelect from './TagSelect';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import useHome from '../../hooks/useHome';
import { useOptionalTags } from '../../contexts/TagsContext';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { applyPrivateTags, removePrivateTags } from '../../services/tagApplications';
import { normalizeId } from '../../utils/ids';
import { privateCollectionTagRefusal, privateTagIdsOnCollection, tagIdOf } from '../../utils/tags';

/**
 * A COLLECTION'S ⋯ → "Tag…" (ruled b53): the person's PRIVATE tags on a
 * collection. Read access of their own is enough (keepr-api: the private
 * store's `canReadCollectionMeta`; the ⋯ offers it through
 * utils/permissions.js canTagCollectionPrivately), and nobody else sees them
 * — "Only you see this". Tags go on and come off as they are picked, through
 * the private store (`PUT` / `DELETE /api/tag-applications`, `targetType:
 * 'collection'`), so the window has one button: Done.
 *
 * - The picker is `TagSelect` in its PRIVATE-ONLY form: my private tags, no
 *   collection tags, no item tags (Home groups collections by private tags
 *   alone — keepr-api homeUtils.collectionGroupsOf).
 * - Which are on it now comes from the one read that says so, Home's
 *   `collectionGroups` (hooks/useHome, asked while the window is open); a
 *   change makes every Home reader read again (FavoritesContext
 *   noteHomeChanged), so Home's groups fill.
 * - Each write waits for the last (the picker is closed meanwhile); a
 *   refusal puts the chips back and says why (a private tag holds at most 50
 *   collections — `personal_tag_full`; `unknown_tag`; `not_found`).
 *
 * Mounted only while open (the caller's choice), so neither Home nor the
 * private tags are asked for until someone opens it.
 */
export default function CollectionTagsModal({ open, onClose, collection = null, collectionId = null }) {
	const id = normalizeId(collectionId) || normalizeId(collection?._id);
	const name = collection?.name || 'Untitled collection';
	const { home, busy: homeBusy, reload: reloadHome } = useHome({ enabled: open });
	const favorites = useOptionalFavorites();
	const tagsContext = useOptionalTags();
	const privateTags = useMemo(() => tagsContext?.tags || [], [tagsContext?.tags]);
	const [applied, setApplied] = useState(null); // tag ids, once Home has answered
	const appliedRef = useRef(null);
	appliedRef.current = applied;
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);

	// Seeded once from Home's answer; after that the window's own writes are
	// the truth it shows (a re-read racing a second pick would flicker).
	useEffect(() => {
		if (applied !== null || !home) return;
		setApplied(privateTagIdsOnCollection(home.collectionGroups, id));
	}, [home, applied, id]);
	const unreadable = home === false;

	const byId = useMemo(() => new Map(privateTags.map(tag => [tagIdOf(tag), tag])), [privateTags]);
	const chips = (applied || []).map(tagId => byId.get(tagId)).filter(Boolean);

	const toggle = async (tagId, on) => {
		const tid = normalizeId(tagId);
		if (!tid || !id || busy) return;
		const before = appliedRef.current || [];
		if (on === before.includes(tid)) return;
		setApplied(on ? [...before, tid] : before.filter(entry => entry !== tid));
		setBusy(true);
		setRefusal(null);
		const rows = [{ tagId: tid, targetType: 'collection', target_id: id }];
		try {
			if (on) await applyPrivateTags(rows);
			else await removePrivateTags(rows);
			favorites?.noteHomeChanged?.();
		} catch (error) {
			setApplied(before);
			const tagName = byId.get(tid)?.name || 'The tag';
			setRefusal(`${on ? `“${tagName}” was not put on ${name}.` : `“${tagName}” was not taken off ${name}.`} ${privateCollectionTagRefusal(error)}`);
		} finally {
			setBusy(false);
		}
	};

	const noPrivateTags = tagsContext?.loaded !== false && !tagsContext?.loading && privateTags.length === 0;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Tags"
			destroyOnClose
			className="collection-tags-modal"
			footer={[<Button key="done" type="primary" onClick={onClose}>Done</Button>]}
		>
			<div className="modal-identity">
				<RecordIdentity
					kicker="Collection"
					badge={<RecordBadge kind="collection" record={collection} title={name} />}
				/>
			</div>
			<p className="meta-line collection-tags__lead">
				Your private tags on this collection. Only you see this: they show on its items as “From the collection”, for you alone.
			</p>
			{refusal ? <Alert type="error" showIcon closable onClose={() => setRefusal(null)} className="collection-tags__refusal" message={refusal} data-testid="collection-tags-refusal" /> : null}
			<DialogLoadState
				loaded={applied !== null}
				loading={unreadable && homeBusy}
				error={unreadable}
				errorDetail="Your private tags on this collection could not be read."
				label="your private tags on this collection"
				onRetry={reloadHome}
				skeleton={<Skeleton variant="line" count={2} />}
				minHeight={96}
			>
				<>
					<TagRow
						tags={chips}
						className="collection-tags__chips"
						label={`Your private tags on ${name}`}
						empty={<span className="meta-line">None of your private tags is on it yet.</span>}
						renderTag={tag => (
							<TagBadge tag={tag} size="sm" onRemove={busy ? undefined : () => toggle(tagIdOf(tag), false)} />
						)}
					/>
					{noPrivateTags ? (
						<p className="collection-tags__empty">
							You have no private tags yet. Add them on <Link to="/account/tags">Private tags</Link>, then come back.
						</p>
					) : (
						<TagSelect
							multiple
							hideSelected
							value={applied || []}
							onSelect={tagId => toggle(tagId, true)}
							onDeselect={tagId => toggle(tagId, false)}
							// ⚑ The private-only form (b53): my private tags alone.
							collectionTags={[]}
							privateTags={privateTags}
							itemTags={false}
							disabled={busy}
							placeholder="Add a private tag"
							aria-label="Add a private tag"
							className="collection-tags__select"
						/>
					)}
				</>
			</DialogLoadState>
		</Modal>
	);
}
